import { useEffect, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';

function HockeyPlayer({ keeper = false }: { keeper?: boolean }) {
  return <g fill="none" stroke="currentColor" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="0" cy="-26" r="9" fill="currentColor" stroke="none" />
    <path d="M-3 -10 L5 6 L-10 28 M5 6 L20 26" />
    {keeper && <path d="M-10 16 L-10 31 M18 16 L22 31" strokeWidth="13" />}
    {/* Long narrow shaft and compact upturned toe, held with both hands. */}
    <path data-hockey-stick="true" d="M18 -3 L30 33 Q32 39 36 37 Q39 35 37 32" stroke="#f8fafc" strokeWidth="3" />
    <path d="M18 -3 L23 12" stroke="#334155" strokeWidth="3.5" />
    <path d="M-1 -8 L10 -1 L20 3 M1 -3 L11 10 L23 12" strokeWidth="5" />
  </g>;
}

/** Decorative field-hockey scene, scaled as one SVG on desktop and mobile. */
export default function MaintenanceHockeyField() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref);
  const reducedMotion = useReducedMotion();
  const running = inView && !reducedMotion;
  const [score, setScore] = useState(0);

  useEffect(() => {
    if (!running) return;
    let interval: ReturnType<typeof setInterval> | undefined;
    const firstGoal = setTimeout(() => {
      setScore(value => value + 1);
      interval = setInterval(() => setScore(value => value + 1), 8000);
    }, 6400);
    return () => { clearTimeout(firstGoal); if (interval) clearInterval(interval); };
  }, [running]);

  const transition = { duration: 8, repeat: Infinity, ease: 'linear' as const,
    times: [0, 0.3, 0.55, 0.7, 0.8, 1] };

  return <div ref={ref} className="relative mb-8 w-full max-w-4xl" data-testid="maintenance-hockey-field" aria-hidden="true">
    <svg viewBox="0 0 900 500" className="block h-auto w-full rounded-lg" fill="none">
      <rect width="900" height="500" rx="14" fill="#0f766e" />
      <rect x="20" y="20" width="860" height="460" rx="2" stroke="#ccfbf1" strokeWidth="3" />
      {/* Halfway and 23-metre lines: field hockey has no centre circle. */}
      <path d="M450 20 V480 M235 20 V480 M665 20 V480" stroke="#ccfbf1" strokeWidth="2" opacity="0.8" />
      {/* Shooting Ds with a straight section parallel to each goal mouth. */}
      <path data-shooting-circles="true" d="M20 90 A140 140 0 0 1 160 230 V270 A140 140 0 0 1 20 410 M880 90 A140 140 0 0 0 740 230 V270 A140 140 0 0 0 880 410" stroke="#ccfbf1" strokeWidth="3" />
      <circle cx="82" cy="250" r="3" fill="#ccfbf1" />
      <circle cx="818" cy="250" r="3" fill="#ccfbf1" />
      {/* Low goals beyond the backlines, with net and backboard. */}
      <path d="M20 230 H5 V270 H20 M880 230 H895 V270 H880" fill="#134e4a" stroke="white" strokeWidth="3" />
      <path d="M885 230 V270 M890 230 V270 M880 240 H895 M880 250 H895 M880 260 H895 M10 230 V270 M15 230 V270 M5 240 H20 M5 250 H20 M5 260 H20" stroke="white" strokeWidth="1" opacity="0.55" />
      <motion.g key={`attacker-${running}`} initial={{ x: 350, y: 300 }}
        animate={running ? { x: [350, 490, 655, 750, 750, 750], y: [300, 290, 265, 222, 222, 222] } : undefined}
        transition={transition} color="#93c5fd"><HockeyPlayer /></motion.g>
      <motion.g key={`keeper-${running}`} initial={{ x: 855, y: 245 }}
        animate={running ? { x: [855, 855, 852, 845, 845, 855], y: [245, 260, 255, 282, 282, 245] } : undefined}
        transition={transition} color="#fb923c"><HockeyPlayer keeper /></motion.g>
      <motion.circle key={`ball-${running}`} data-hockey-ball="true" r="5" fill="white" stroke="#cbd5e1" strokeWidth="1.5"
        initial={{ cx: 394, cy: 336 }}
        animate={running ? { cx: [394, 534, 699, 794, 888, 888], cy: [336, 326, 301, 258, 250, 250] } : undefined}
        transition={transition} />
      {running && <motion.g initial={{ opacity: 0 }} animate={{ opacity: [0, 0, 0, 0, 1, 0] }} transition={transition}>
        <rect x="350" y="195" width="200" height="76" rx="38" fill="white" />
        <text x="435" y="244" textAnchor="middle" fontSize="32" fontWeight="700" fill="#134e4a">GOAL!</text>
        <path d="M504 211 L515 247 Q517 253 521 251 Q524 249 522 246" stroke="#ea580c" strokeWidth="3.5" strokeLinecap="round" />
        <circle cx="486" cy="254" r="5" fill="#f8fafc" stroke="#64748b" strokeWidth="1.5" />
      </motion.g>}
    </svg>
    <div className="absolute right-4 top-4 rounded-full bg-white px-3 py-1 text-sm font-bold tabular-nums text-gray-800 sm:text-lg">{score} - 0</div>
  </div>;
}
